import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Loader2, Save } from 'lucide-react';
import { useAdminProducerContext } from '@/context/AdminProducerContext';
import { toast } from '@/hooks/use-toast';
import {
  ContactSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { useProducerFormData } from '@/hooks/useProducerFormData';

export default function AdminProducerKontakt() {
  const { producerId } = useParams<{ producerId: string }>();
  const { getProducerById, updateProducer } = useAdminProducerContext();
  const [producer, setProducer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProducer = async () => {
      if (!producerId) return;
      setIsLoading(true);
      try {
        const data = await getProducerById(producerId);
        setProducer(data || null);
      } catch (err) {
        console.error('Failed to load producer:', err);
        setProducer(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducer();
  }, [producerId, getProducerById]);

  const { initialFormData } = useProducerFormData(producer, false, producerId);

  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async (data) => {
      if (!producer) return;
      await updateProducer(producer.id, {
        phone: data.phone || undefined,
        email: data.email || undefined,
        website: data.website || undefined,
        instagram: data.instagram || undefined,
        openingHoursText: data.openingHoursText || undefined,
      });
      toast({ title: 'Sparat', description: 'Kontakt har uppdaterats.' });
    },
  });

  if (isLoading) {
    return (
      <AdminLayout producerId={producerId}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (!producer) {
    return (
      <AdminLayout producerId={producerId}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Producenten hittades inte.</AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout producerId={producerId} producerName={producer.name} title="Kontakt">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Kontakt</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Telefon, e-post, webbplats och öppettider.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {editorState.isDirty && (
            <span className="text-xs text-amber-600 font-medium">Osparade ändringar</span>
          )}
          <Badge variant={producer.status === 'public' ? 'default' : 'secondary'}>
            {producer.status === 'public' ? 'Publicerad' : 'Utkast'}
          </Badge>
          <Button
            size="sm"
            onClick={editorState.save}
            disabled={editorState.isSaving || !editorState.isDirty}
          >
            {editorState.isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sparar...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Spara
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="max-w-2xl">
        <ContactSection
          formData={editorState.formData}
          updateField={editorState.updateField}
        />
      </div>
    </AdminLayout>
  );
}
